import type { OrgCustomColors } from '@cognitoforms/types/server-types/model/org-custom-colors';
import iro from '@jaames/iro';
import ColorContrastChecker from 'color-contrast-checker';
import { rgbToHex, rgbStringToObject, darkenRGB, lightenRGB, mixRGB } from '@cognitoforms/utils/color-utils';
import type { OrgAppearanceSettingsMeta } from '@cognitoforms/types/server-types/model/org-appearance-settings-meta';
import { ColorScheme } from '@cognitoforms/types/server-types/model/color-scheme';

// Default helpers functions
export function getDefaults() {
	return {
		Text: 'rgb(35,70,82)',
		Background: 'rgb(255,255,255)',
		Primary: 'rgb(9,160,152)',
		Secondary: 'rgb(148,204,63)',
		Important: 'rgb(216,84,39)'
	};
}

export function isDefaultColors(colors: OrgCustomColors) {
	if (!colors)
		return true;
	const defaultColors = getDefaults();
	return colors.Text.replace(/\s+/g, '') === defaultColors.Text
		&& colors.Background.replace(/\s+/g, '') === defaultColors.Background
		&& colors.Primary.replace(/\s+/g, '') === defaultColors.Primary
		&& colors.Secondary.replace(/\s+/g, '') === defaultColors.Secondary
		&& colors.Important.replace(/\s+/g, '') === defaultColors.Important;
}

// Contrast compatibility helper functions
export function isAACompliant(color1: string, color2: string) {
	const colorChecker = new ColorContrastChecker();
	return colorChecker.isLevelAA(rgbToHex(color1), rgbToHex(color2));
}

export function isCustomCompliant(color1: string, color2: string, ratio: number) {
	const colorChecker = new ColorContrastChecker();
	return colorChecker.isLevelCustom(rgbToHex(color1), rgbToHex(color2), ratio);
}

// Updating Custom Theme functions
/** Compare the relative darkness of two colors
 * @param color1 string in the form rgb(r,g,b)
 * @param color2 string in the form rgb(r,g,b)
 * @returns false if color1 is darker than color2, true otherwise
 */
function calculateRelativeDarkness(color1: string, color2: string) {
	const color1Obj = rgbStringToObject(color1);
	const color2Obj = rgbStringToObject(color2);

	const hsv1 = iro.Color.rgbToHsv(color1Obj);
	const hsv2 = iro.Color.rgbToHsv(color2Obj);
	return hsv1.v > hsv2.v;
}

function calculateIsColorDark(color: string) {
	const colorObj = rgbStringToObject(color);
	const hsv = iro.Color.rgbToHsv(colorObj);
	return hsv.v <= 50;
}

function calculateDerivedColor(color: string, derivedColor: string, isDark: boolean, hasReversed: boolean = false): [string, boolean, boolean] {
	derivedColor = isDark ? lightenRGB(derivedColor) : darkenRGB(derivedColor);

	// If we hit black or white and it's still not compliant, reverse direction
	if (derivedColor === 'rgb(0, 0, 0)' || derivedColor === 'rgb(255, 255, 255)') {
		// Sanity check to prevent infinite loop, if somehow it is not compliant with either white or black,
		// return either white or black based on which direction it originally went
		if (hasReversed) // returning the opposite of isDark since we know it flipped
			return isDark ? ['rgb(0, 0, 0)', isDark, hasReversed] : ['rgb(255, 255, 255)', isDark, hasReversed];
		else {
			isDark = !isDark;
			derivedColor = color;
			hasReversed = true;
		}
	}

	return [derivedColor, isDark, hasReversed];
}

function getCompliantColor(color: string, baseContrastColor: string | null = null, customRatio: number | null = null) {
	let derivedColor = color;
	let isDark = baseContrastColor ? calculateRelativeDarkness(baseContrastColor, color) : calculateIsColorDark(color);

	// if the color is 'dark', and the base constrast color was not specified, use white as the text color
	if (isDark && !baseContrastColor)
		return 'rgb(255, 255, 255)';

	let hasReversed = false;

	if (customRatio) // If custom ratio is specified, we will derive the color until it is compliant with the custom ratio (ex. primary icons on bg)
		while (!isCustomCompliant(derivedColor, baseContrastColor ?? color, customRatio))
			[derivedColor, isDark, hasReversed] = calculateDerivedColor(color, derivedColor, isDark, hasReversed);
	else // else, derive the color until it is AA compliant
		while (!isAACompliant(derivedColor, baseContrastColor ?? color))
			[derivedColor, isDark, hasReversed] = calculateDerivedColor(color, derivedColor, isDark, hasReversed);

	return derivedColor;
}

export function calculateIsDark (hasCustomTheme: boolean, useSystemColors: boolean, colors: OrgCustomColors, userColorScheme: ColorScheme) {
	// If the org has color customization, rely on that first
	if (hasCustomTheme && colors)
		return calculateRelativeDarkness(colors.Text, colors.Background);
	// If the user has a color scheme set, use that
	else if (useSystemColors) {
		if (userColorScheme === ColorScheme.System)
			return window.matchMedia('(prefers-color-scheme: dark)').matches;

		return userColorScheme === ColorScheme.Dark;
	}
	// If the org has no custom theme and user has no color scheme, return false for dark
	return false;
}

export function getDerivedColors(colors: OrgCustomColors) {
	if (!colors || isDefaultColors(colors)) {
		return {
			derivedTextForPrimary: 'initial',
			derivedTextForImportant: 'initial',
			derivedPrimaryTextForBackground: 'initial',
			derivedPrimaryIconForBackground: 'initial'
		};
	}

	// Calculate custom compliant colors, these calculate what text to use on a specific background color (mostly button text)
	// For text color on an important/primary background, we do not want to derive the text color if it is the default color
	const derivedTextForPrimary = colors.Primary === getDefaults().Primary ? 'initial' : getCompliantColor(colors.Primary); // the text color we use on a primary background
	const derivedTextForImportant = colors.Important === getDefaults().Important ? 'initial' : getCompliantColor(colors.Important); // the text color we use on an important background

	// the colors below are sometimes used as text on a background that is a mix of the background and text colors, so we need to make sure it is compliant with the text color on the background
	const mixTextAndBackground = mixRGB(colors.Background, colors.Text, .93);
	const derivedPrimaryTextForBackground = getCompliantColor(colors.Primary, mixTextAndBackground); // the text color we use on background, derived from the primary color
	const derivedPrimaryIconForBackground = getCompliantColor(colors.Primary, mixTextAndBackground, (3 / 1)); // the icon color we use on background, derived from the primary color

	return {
		derivedTextForPrimary,
		derivedTextForImportant,
		derivedPrimaryTextForBackground,
		derivedPrimaryIconForBackground
	};
}

export function getCustomColorsFromCss() {
	const computedStyle = getComputedStyle(document.documentElement);
	const orgColors = {
		Text: computedStyle.getPropertyValue('--text').trim(),
		Background: computedStyle.getPropertyValue('--background').trim(),
		Primary: computedStyle.getPropertyValue('--primary').trim(),
		Secondary: computedStyle.getPropertyValue('--secondary').trim(),
		Important: computedStyle.getPropertyValue('--important').trim()
	} as OrgCustomColors;

	// Fallback to defaults if any color is not set
	if (Object.values(orgColors).some(color => color === ''))
		return getDefaults() as OrgCustomColors;

	return orgColors;
}

function getUserColorSchemeFromCss() {
	const colorScheme = getComputedStyle(document.documentElement).getPropertyValue('--user-color-scheme');
	if (colorScheme === 'dark')
		return ColorScheme.Dark;
	else if (colorScheme === 'light')
		return ColorScheme.Light;
	else if (colorScheme === 'system')
		return ColorScheme.System;
	return null;
}

export function updateCustomTheme(useSystemColors: boolean, colors: OrgCustomColors, userColorScheme: ColorScheme) {
	if (!colors)
		colors = getCustomColorsFromCss();

	const hasCustomTheme = !isDefaultColors(colors);
	if (hasCustomTheme) {
		document.documentElement.style.setProperty('--text', colors.Text);
		document.documentElement.style.setProperty('--background', colors.Background);
		document.documentElement.style.setProperty('--primary', colors.Primary);
		document.documentElement.style.setProperty('--secondary', colors.Secondary);
		document.documentElement.style.setProperty('--important', colors.Important);

		const derivedColors = getDerivedColors(colors);
		document.documentElement.style.setProperty('--derived-text-for-primary', derivedColors.derivedTextForPrimary);
		document.documentElement.style.setProperty('--derived-text-for-important', derivedColors.derivedTextForImportant);
		document.documentElement.style.setProperty('--derived-primary-text-for-background', derivedColors.derivedPrimaryTextForBackground);
		document.documentElement.style.setProperty('--derived-primary-icon-for-background', derivedColors.derivedPrimaryIconForBackground);
		document.documentElement.setAttribute('data-has-custom-theme', 'true');
	}
	else {
		document.documentElement.style.setProperty('--text', 'initial');
		document.documentElement.style.setProperty('--background', 'initial');
		document.documentElement.style.setProperty('--primary', 'initial');
		document.documentElement.style.setProperty('--secondary', 'initial');
		document.documentElement.style.setProperty('--important', 'initial');
		document.documentElement.style.setProperty('--derived-text-for-primary', 'initial');
		document.documentElement.style.setProperty('--derived-text-for-important', 'initial');
		document.documentElement.style.setProperty('--derived-primary-text-for-background', 'initial');
		document.documentElement.style.setProperty('--derived-primary-icon-for-background', 'initial');

		document.documentElement.setAttribute('data-has-custom-theme', 'false');
	}

	// If colorScheme is not passed in, get it from the CSS
	if (!userColorScheme)
		userColorScheme = getUserColorSchemeFromCss();

	document.documentElement.setAttribute('data-color-scheme', calculateIsDark(hasCustomTheme, useSystemColors, colors, userColorScheme) ? 'dark' : 'light');
}

// Update Default Typeface
export function updateTypeface(useDefaultTypeface: boolean) {
	if (useDefaultTypeface === null) {
		// get the default value from the CSS
		useDefaultTypeface = getComputedStyle(document.documentElement).getPropertyValue('--font-display').trim() === 'system-ui';
	}
	else if (useDefaultTypeface)
		document.documentElement.style.setProperty('--font-display', 'system-ui');
	else
		document.documentElement.style.removeProperty('--font-display');
}

// JTODO update logo

// Update Org Appearance
export function updateOrgAppearance(useSystemColors: boolean, orgAppearanceSettings: OrgAppearanceSettingsMeta, userColorScheme: ColorScheme) {
	updateCustomTheme(useSystemColors, orgAppearanceSettings?.Colors, userColorScheme);
	updateTypeface(orgAppearanceSettings?.UseDefaultTypeface);
}